import { useMemo } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { CommonAPI } from "../types";
import { eventStreamQueries } from "../queries";
import DisplayMoment from "../session/DisplayMoment";
import { Form, Spinner } from "react-bootstrap";
import { AgencyTagFilter } from "../tag/AgencyTagFilter";
import {
  Fields,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  Toolbar,
} from "@bagel-web/components";
import useFilters from "../filter/useFilters";
import {
  getMessageTableColumnVisibility,
  MessageTableHeader,
} from "../session/message/Message";
import MessageTypeSelect from "../session/MessageTypeSelect";
import useShowMessageTypes from "../session/useShowMessageTypes";
import { LocalStorageKeys } from "../util/localStorage";
import { useAgentViewPreferences } from "./viewPreferences/agentViewPreferences";
import MessageOrderingToggle from "./viewPreferences/MessageOrderingToggle";

const AgentMomentMessages = ({
  sessionId,
  agentId,
  momentId,
  commonAPI,
  agencyTagFilter,
}: {
  sessionId: string;
  agentId: string;
  momentId: number;
  commonAPI: CommonAPI;
  agencyTagFilter: AgencyTagFilter;
}) => {
  const { messageStreamTextFilter, setMessageStreamTextFilter } =
    useAgentViewPreferences();

  const filterFields = useMemo<Fields>(
    () => [
      {
        name: "messageTypes",
        label: "Message Types",
        type: FieldType.MessageType,
        Component: (props) => (
          <MessageTypeSelect
            {...props}
            fetchMessageTypes={commonAPI.fetchMessageTypes}
          />
        ),
        colSpan: 2,
      },
    ],
    [commonAPI.fetchMessageTypes]
  );

  const { filterCriteria, handleSetFilters, handleClearFilter } = useFilters(
    filterFields,
    LocalStorageKeys.Filters.AGENT_HISTORY_MESSAGES
  );

  const showMessageTypes = useShowMessageTypes(
    commonAPI.fetchMessageTypes,
    filterCriteria["messageTypes"]?.value
  );

  const { isPending, data: eventStreamDetail } = useQuery({
    queryKey: eventStreamQueries.getDetail(sessionId, agentId, momentId),
    queryFn: () =>
      commonAPI.fetchEventStreamDetail(sessionId, agentId, momentId),
    staleTime: Infinity,
  });

  const columnVisibility = useMemo(
    () =>
      getMessageTableColumnVisibility(
        eventStreamDetail ? [eventStreamDetail] : []
      ),
    [eventStreamDetail]
  );

  return (
    <div>
      <Toolbar className="mb-3">
        <Form.Control
          type="search"
          value={messageStreamTextFilter}
          placeholder="Filter by message content..."
          className="font-monospace"
          style={{ fontSize: "0.875rem" }}
          onChange={(e) => setMessageStreamTextFilter(e.target.value)}
        />
        <FixedToolbarItem>
          <MessageOrderingToggle />
        </FixedToolbarItem>
        <FixedToolbarItem>
          <FilterBar
            fields={filterFields}
            criteria={filterCriteria}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
            showChips={false}
          />
        </FixedToolbarItem>
        <FixedToolbarItem>
          <Link
            href={`/sessions/${sessionId}/agent/${agentId}/event-stream`}
            className="card-link"
          >
            View Full Message Stream
          </Link>
        </FixedToolbarItem>
      </Toolbar>
      {isPending && <Spinner className="mt-3" />}
      {eventStreamDetail && (
        <table style={{ width: "100%" }}>
          <MessageTableHeader columnVisibility={columnVisibility} />
          <tbody>
            <DisplayMoment
              agentId={agentId}
              sessionId={sessionId}
              moment={eventStreamDetail}
              commonAPI={commonAPI}
              agencyTagFilter={agencyTagFilter}
              showMessageTypes={showMessageTypes}
              columnVisibility={columnVisibility}
            />
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AgentMomentMessages;
